const API_ORIGIN = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";
/** Full API base URL including global prefix (e.g. http://localhost:3001/api/v1) */
export const API_BASE = `${API_ORIGIN}/api/v1`;

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ApiResponse<T> {
  data?: T;
  meta?: PaginationMeta;
  summary?: Record<string, unknown>;
  message?: string;
  statusCode?: number;
}

let accessToken: string | null = null;
let refreshToken: string | null = null;

export function setTokens(access: string, refresh: string) {
  accessToken = access;
  refreshToken = refresh;
  if (typeof window !== "undefined") {
    localStorage.setItem("accessToken", access);
    localStorage.setItem("refreshToken", refresh);
  }
}

export function getAccessToken(): string | null {
  if (typeof window !== "undefined" && !accessToken) {
    accessToken = localStorage.getItem("accessToken");
    refreshToken = localStorage.getItem("refreshToken");
  }
  return accessToken;
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  if (typeof window !== "undefined") {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  }
}

async function doRefresh(): Promise<boolean> {
  const rt =
    typeof window !== "undefined"
      ? localStorage.getItem("refreshToken")
      : refreshToken;
  if (!rt) return false;
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: rt }),
  });
  if (!res.ok) return false;
  const json = await res.json();
  const nextAccess = json.accessToken ?? json.data?.accessToken;
  const nextRefresh = json.refreshToken ?? json.data?.refreshToken;
  if (nextAccess) {
    setTokens(nextAccess, nextRefresh ?? rt);
    return true;
  }
  return false;
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const url = path.startsWith("http")
    ? path
    : `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
  if (path.includes("pos/")) {
    console.log("[POS API]", (options.method ?? "GET"), url);
  }
  const token = getAccessToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token)
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;

  let res = await fetch(url, { ...options, headers });
  if (path.includes("pos/")) {
    console.log("[POS API] response", res.status, url);
  }

  if (res.status === 401 && refreshToken && (await doRefresh())) {
    const newToken = getAccessToken();
    if (newToken)
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${newToken}`;
    res = await fetch(url, { ...options, headers });
  }

  const text = await res.text();
  let parsed: { data?: T; meta?: PaginationMeta; summary?: Record<string, unknown>; message?: string } | undefined;
  try {
    parsed = text
      ? (JSON.parse(text) as {
          data?: T;
          meta?: PaginationMeta;
          summary?: Record<string, unknown>;
          message?: string;
        })
      : undefined;
  } catch {
    // ignore
  }

  if (!res.ok) {
    return {
      data: parsed?.data as T,
      message: parsed?.message ?? res.statusText,
      statusCode: res.status,
    };
  }
  return {
    data: (parsed as { data?: T })?.data ?? (parsed as T),
    meta: parsed?.meta,
    summary: parsed?.summary,
    statusCode: res.status,
  };
}

export async function apiGet<T>(path: string): Promise<ApiResponse<T>> {
  return api<T>(path, { method: "GET" });
}

export async function apiPost<T>(
  path: string,
  body: unknown,
): Promise<ApiResponse<T>> {
  return api<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
): Promise<ApiResponse<T>> {
  return api<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}

export async function apiDelete<T>(path: string): Promise<ApiResponse<T>> {
  return api<T>(path, { method: "DELETE" });
}

/** Normalize API response to an array (handles { data: T[] } or raw array). */
export function getListFromResponse<T>(res: ApiResponse<T[] | T> | unknown): T[] {
  if (!res || typeof res !== "object") return [];
  const r = res as { data?: T[] | T };
  if (Array.isArray(r.data)) return r.data;
  if (Array.isArray(r)) return r as T[];
  return [];
}

/** Download stock count Excel (CSV) file. Triggers browser download. */
export async function downloadStockCountExcel(id: string, defaultFilename = "stock_count.csv"): Promise<void> {
  const token = getAccessToken();
  const url = `${API_BASE}/stock-counts/${id}/excel`;
  const res = await fetch(url, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(res.statusText || "Download failed");
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition");
  let filename = defaultFilename;
  const match = disposition?.match(/filename="?([^";\n]+)"?/);
  if (match?.[1]) filename = match[1].trim();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Download products import template (xlsx). Triggers browser download. */
export async function downloadProductsImportTemplate(): Promise<void> {
  const token = getAccessToken();
  const url = `${API_BASE}/products/import/template`;
  const res = await fetch(url, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(res.statusText || "Download failed");
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition");
  const filename = disposition?.match(/filename="?([^";\n]+)"?/)?.[1]?.trim() ?? "products_import_template.xlsx";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export interface ProductsImportResult {
  created: number;
  errors: { row: number; message: string }[];
}

/** Import products from uploaded Excel file. */
export async function importProductsFile(file: File): Promise<ApiResponse<ProductsImportResult>> {
  const token = getAccessToken();
  const url = `${API_BASE}/products/import`;
  const formData = new FormData();
  formData.append("file", file);
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });
  const text = await res.text();
  let parsed: { data?: ProductsImportResult | { data?: ProductsImportResult; message?: string }; message?: string } | undefined;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    // ignore
  }
  const payload = parsed?.data;
  const resultData =
    payload && typeof payload === 'object' && 'created' in payload && 'errors' in payload
      ? (payload as ProductsImportResult)
      : (payload as { data?: ProductsImportResult })?.data;
  if (!res.ok) {
    return {
      data: resultData,
      message: parsed?.message ?? (payload as { message?: string })?.message ?? res.statusText,
      statusCode: res.status,
    };
  }
  return {
    data: resultData ?? (parsed as unknown as ProductsImportResult),
    message: (payload as { message?: string })?.message ?? parsed?.message,
    statusCode: res.status,
  };
}
